<template>
  <LoadingOverlayComponent v-if="isLoadingDashboardData" />
  <div class="mw-100 overflow-y-auto flex-grow-1 container pt-4 pb-12">
    <div class="container">
      <!-- you can't submit invoices for consolidated dashboard so has-pending-invoices is false -->
      <DashboardNavigationComponent
        :title="$t('dashboard.consolidated_view')"
        :devices-total="filteredRegistryDevices.length"
        :power-total="
          filteredRegistryDevices.reduce(
            (sum, device) => sum + device.device_power,
            0
          )
        "
        :pending-payouts="pendingPayouts"
        :can-submit-invoice="false"
        :certificates-count="portfolioItemsStore.totalAvailableGOs"
        :projected-production="totalProjectedProduction"
        :is-loading="isLoadingDashboardData"
        :is-single-company="false"
      />

      <!-- Company Filter Section -->
      <div class="card border-translucent mb-4">
        <div
          class="card-body d-flex align-items-start justify-content-between flex-wrap gap-3"
        >
          <div class="d-flex align-items-center gap-3">
            <span
              class="company-filter-icon d-inline-flex align-items-center justify-content-center rounded-circle bg-primary-subtle text-primary"
            >
              <FontAwesomeIcon icon="layer-group" />
            </span>
            <div>
              <p class="mb-1 text-uppercase fw-semibold text-secondary small">
                {{ $t("common.companies") }}
              </p>
              <p class="mb-0 text-body-secondary">
                {{ $t("dashboard.consolidated.company_filter_hint") }}
              </p>
            </div>
          </div>
          <div
            class="filter-select company-filter-select flex-grow-1 flex-md-grow-0"
          >
            <ConsolidatedCompanySelector
              v-model="selectedCompanyIds"
              :companies="allCompaniesWithIds"
            />
          </div>
        </div>
      </div>

      <!-- Empty state when every company has been deselected -->
      <div v-if="hasNoCompaniesSelected" class="card border-translucent">
        <div class="card-body text-center py-6">
          <span
            class="company-filter-icon d-inline-flex align-items-center justify-content-center rounded-circle bg-primary-subtle text-primary mb-3"
          >
            <FontAwesomeIcon icon="layer-group" />
          </span>
          <h5 class="mb-2">
            {{ $t("dashboard.consolidated.no_companies_selected_title") }}
          </h5>
          <p class="text-body-secondary mb-0">
            {{ $t("dashboard.consolidated.no_companies_selected_hint") }}
          </p>
          <button
            type="button"
            class="btn btn-outline-primary btn-sm mt-3"
            @click="selectAllCompanies"
          >
            {{ $t("common.select_all") }}
          </button>
        </div>
      </div>

      <!-- Tab content -->
      <div v-else class="tab-content">
        <KeepAlive :max="3">
          <ConsolidatedCertificatesTab
            v-if="activeTab === 'certificates'"
            :key="'certificates-' + selectedCompanyIds.join(',')"
            :selectedCompanyIds
            :companiesWithIds
            :isAdmin
            :accountData
          />
          <ConsolidatedTradingStrategyTab
            v-else-if="activeTab === 'trading-strategy'"
            :key="'trading-strategy-' + selectedCompanyIds.join(',')"
            :companies="companiesWithIds"
            :registryDevices="filteredRegistryDevices"
          />
          <ConsolidatedInvoiceHistoryTab
            v-else-if="activeTab === 'invoice-history'"
            :key="'invoice-history-' + selectedCompanyIds.join(',')"
            :companies="companiesWithIds"
            :isAdmin
          />
        </KeepAlive>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useAccount } from "@/composables/account/useAccount";
import {
  calculateTotalProjection,
  parseProjectionsFromApi,
} from "@/utils/projections";
import { debounce, filter } from "lodash-es";
import { computed, onUnmounted, ref, watch } from "vue";

import LoadingOverlayComponent from "@/components/common/LoadingOverlayComponent.vue";
import { pendingPayoutsByPayoutCurrency } from "@/composables/money/invoiceUtils";
import { goSolidApi } from "@/composables/useGoSolidApi";
import { useQueryManager } from "@/composables/useQueryManager";
import ConsolidatedCertificatesTab from "@/dashboard/consolidated/ConsolidatedCertificatesTab.vue";
import ConsolidatedCompanySelector from "@/dashboard/consolidated/ConsolidatedCompanySelector.vue";
import ConsolidatedInvoiceHistoryTab from "@/dashboard/consolidated/ConsolidatedInvoiceHistoryTab.vue";
import ConsolidatedTradingStrategyTab from "@/dashboard/consolidated/ConsolidatedTradingStrategyTab.vue";
import DashboardNavigationComponent from "@/dashboard/DashboardNavigationComponent.vue";
import { usePortfolioItemsStore } from "@/dashboard/inventory_management/common/usePortfolioItemsStore";
import type { Account, Company, InvoiceItem, RegistryDevice } from "@/types";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import { useGlobalHandlers } from "@/composables/useGlobalHandlers";

// Serialize array query params as repeated keys (company_ids=1&company_ids=2),
// which is the format the backend's CompanyQueryParams expects.
const REPEATED_ARRAY_PARAMS_SERIALIZER = { indexes: null };

const { globalErrorHandler } = useGlobalHandlers();

const invoiceItemsPending = ref<InvoiceItem[]>([]);
const registryDevices = ref<RegistryDevice[]>([]);
const isLoadingDashboardData = ref(false);
const totalProjectedProduction = ref(0);
const selectedCompanyIds = ref<number[]>([]);
const allPortfolioItems = ref<any[]>([]); // Store unfiltered portfolio items

const portfolioItemsStore = usePortfolioItemsStore();

// Clear the cache when consolidated component mounts
// This prevents showing single company data in consolidated view
portfolioItemsStore.setAllItems([]);

const { getQueryValue, parsers } = useQueryManager();
const activeTab = getQueryValue<string>(
  "tab",
  parsers.stringWithDefault("certificates")
);

const { accountData } = useAccount(fetchDashboardData);

async function fetchDashboardData(fetchedAccountData: Account) {
  // Define fetching logic for portfolio items separately so it can be reused for refetching
  async function fetchPortfolioItems() {
    try {
      const response = await goSolidApi.get(
        "/api/companies/flattened_portfolio_items"
      );
      allPortfolioItems.value = response.data.portfolio_items ?? [];
      portfolioItemsStore.setSourceHasMultipleCompanies(
        allPortfolioItems.value
      );
    } catch (err) {
      globalErrorHandler(err);
      allPortfolioItems.value = [];
      portfolioItemsStore.setSourceHasMultipleCompanies([]);
    }
  }

  isLoadingDashboardData.value = true;
  if (!fetchedAccountData || !fetchedAccountData.id) {
    console.error(
      "Account data or ID is missing, cannot fetch dashboard data."
    );
    isLoadingDashboardData.value = false;
    return;
  }

  // Register the refetch action so that child components (e.g. virtual portfolio cards)
  // can trigger a refresh of the consolidated view when needed.
  portfolioItemsStore.registerRefetch(fetchPortfolioItems);

  try {
    const [invoiceItemsPendingRes, registryDevicesRes] = await Promise.all([
      goSolidApi.get("/api/account/invoice-items-pending").catch((err) => {
        globalErrorHandler(err);
        return { data: { invoice_items: [] } };
      }),
      goSolidApi.get("/api/account/registry-devices").catch((err) => {
        globalErrorHandler(err);
        return { data: { registry_devices: [] } };
      }),
      fetchPortfolioItems(),
    ]);

    invoiceItemsPending.value = invoiceItemsPendingRes.data.invoice_items;
    registryDevices.value = registryDevicesRes.data.registry_devices;

    // portfolio items are already set by fetchPortfolioItems() which updates allPortfolioItems.value
    // The watcher on filteredPortfolioItems will then update the store.

    // Explicitly initialize store in case watcher doesn't trigger immediately or empty
    if (allPortfolioItems.value.length === 0) {
      portfolioItemsStore.setAllItems([]);
    }

    // Projected production is recomputed from the company selection — see the
    // watcher on selectedCompanyIds below.
  } catch (err: any) {
    globalErrorHandler(err);
  } finally {
    isLoadingDashboardData.value = false;
  }
}

const allCompaniesWithIds = computed(() => {
  return filter(
    accountData.companies,
    (company): company is Company & { id: number } =>
      typeof company.id === "number"
  );
});

const selectedIdSet = computed(() => new Set(selectedCompanyIds.value));

// The selection is authoritative: an empty selection means "no companies",
// not "all". The dashboard then shows an empty state (see hasNoCompaniesSelected).
const companiesWithIds = computed(() =>
  allCompaniesWithIds.value.filter((company) =>
    selectedIdSet.value.has(company.id)
  )
);

// True once companies have loaded but the user has deselected every one.
const hasNoCompaniesSelected = computed(
  () =>
    allCompaniesWithIds.value.length > 0 &&
    selectedCompanyIds.value.length === 0
);

// Portfolio items for the current selection; an empty selection yields none.
const filteredPortfolioItems = computed(() =>
  allPortfolioItems.value.filter((item) =>
    selectedIdSet.value.has(item.company_id)
  )
);

const selectAllCompanies = () => {
  selectedCompanyIds.value = allCompaniesWithIds.value.map(
    (company) => company.id
  );
};

// Update the store with filtered items when selection changes
watch(
  filteredPortfolioItems,
  (items) => {
    // Update the store's displayed items to reflect the filtered companies
    portfolioItemsStore.setAllItems(items);
  },
  { immediate: true }
);

// Header metrics follow the company selection, like certificates and the
// projection do — devices, power and pending payout are scoped to the selected
// companies (both RegistryDevice and InvoiceItem carry company_id). An empty
// selection zeroes them, matching the empty state shown below.
const filteredRegistryDevices = computed(() =>
  registryDevices.value.filter((device) =>
    selectedIdSet.value.has(device.company_id)
  )
);

const pendingPayouts = computed(() => {
  const pendingForSelection = invoiceItemsPending.value.filter((item) =>
    selectedIdSet.value.has(item.company_id)
  );
  return pendingPayoutsByPayoutCurrency(
    pendingForSelection,
    companiesWithIds.value
  );
});

const isAdmin = computed(() => {
  return !!accountData.admin_account_id;
});

// Initialize the selection to all companies the first time they load. Guarded
// by a one-shot flag rather than "selection is empty" so a deliberate clear-all
// is never undone if the companies list is later refreshed.
let selectionInitialized = false;
watch(
  () => accountData.companies,
  (companies) => {
    if (!selectionInitialized && companies?.length) {
      selectAllCompanies();
      selectionInitialized = true;
    }
  },
  { immediate: true }
);

// Projections aren't broken down per company in the portfolio items, so we
// refetch them for the current selection (the endpoint accepts company_ids).
// A monotonically increasing token discards out-of-order responses.
let projectionRequestSeq = 0;

async function fetchProjectedProduction(
  companyIds: number[],
  requestId: number
) {
  try {
    const response = await goSolidApi.get("/api/companies/projections", {
      params: { company_ids: companyIds },
      paramsSerializer: REPEATED_ARRAY_PARAMS_SERIALIZER,
    });
    if (requestId !== projectionRequestSeq) {
      return; // a newer selection has superseded this request
    }
    totalProjectedProduction.value = calculateTotalProjection(
      parseProjectionsFromApi(response.data.projection)
    );
  } catch (err) {
    if (requestId === projectionRequestSeq) {
      globalErrorHandler(err);
    }
  }
}

// Debounced so rapid toggling (e.g. select/clear all) fires a single request.
const debouncedFetchProjectedProduction = debounce(
  fetchProjectedProduction,
  250
);

watch(
  selectedCompanyIds,
  (ids) => {
    // Claim the request token synchronously on *every* change so an in-flight
    // response for a prior selection is discarded the instant the selection
    // changes — not 250ms later when the debounced fetch finally runs. The
    // token is captured per-request, so the latest selection always wins and
    // earlier in-flight responses are dropped when they resolve.
    const requestId = ++projectionRequestSeq;

    // Clearing is authoritative and immediate: zero the header now and drop any
    // pending fetch. The bumped token above already supersedes any in-flight
    // request, so a slow prior response can no longer repaint the cleared view.
    if (ids.length === 0) {
      debouncedFetchProjectedProduction.cancel();
      totalProjectedProduction.value = 0;
      return;
    }

    debouncedFetchProjectedProduction([...ids], requestId);
  },
  { immediate: true }
);

onUnmounted(() => {
  // Drop any pending debounce and bump the token so a late, already in-flight
  // response can't write to a detached ref or surface an error toast after the
  // view has been navigated away from.
  debouncedFetchProjectedProduction.cancel();
  projectionRequestSeq++;
});
</script>

<style scoped lang="scss">
.company-filter-icon {
  width: 44px;
  height: 44px;
  font-size: 1.1rem;
}

.company-filter-icon svg {
  width: 1em;
  height: 1em;
}

.filter-select :deep(.form-control) {
  min-height: 44px;
}

// `min(100%, 440px)` keeps the selector roomy on desktop without forcing
// horizontal overflow on viewports narrower than 440px.
.company-filter-select {
  min-width: min(100%, 440px);
  max-width: 600px;
}

@media (max-width: 768px) {
  .filter-select {
    width: 100%;
  }
}
</style>
